import { createSlice } from '@reduxjs/toolkit';
import MojitoCore from 'mojito/core';
import { isEmpty, without, uniq, omit, pick } from 'mojito/utils';
import ServicesTypes from 'services/common/types.js';
import ServicesUtils from 'services/common/utils.js';
import { actions as bootstrapActions } from 'services/bootstrap/slice.js';
import { isLoggedIn } from 'services/authentication/selectors.js';
import PromotionsDataDescriptor from './descriptor.js';
import serviceFactory from './service-factory';
import SubscriptionsRegistry from './subscriptions-registry';
import { shallowEqual } from 'react-redux';
import PromotionUtils from './utils';
import * as PromotionTypes from './types.js';
import { selectLoadingState } from './selectors';

const { PROMOTIONS } = PromotionsDataDescriptor.DATA_TYPES;
const { actionsRegistry } = MojitoCore.Services.Content;
const { AVAILABLE, UNAVAILABLE, PENDING } = ServicesTypes.CONTENT_STATE;
const reduxInstance = MojitoCore.Services.redux;
const subscriptionsRegistry = new SubscriptionsRegistry();

/**
 * Defines the structure of the promotions' state object.
 *
 * @typedef PromotionsState
 *
 * @property {object} promotionsPerChannel - Contains promotions information organized by channels.
 * @property {object} promotions - Contains promotions information organized by locations (e.g., top, left, event_cards etc.). This object is built by merging promotions from all active channels.
 * @property {Mojito.Services.Common.types.CONTENT_STATE} loadingState - Loading state of the promotions.
 *
 * @memberof Mojito.Services.Promotions
 */

/**
 * The name of the promotions store. Will be used to register in global redux store.
 *
 * @constant
 * @type {string}
 * @memberof Mojito.Services.Promotions
 */
export const STORE_KEY = PromotionTypes.STORE_KEY;

/**
 * The name of the promotions store. Will be used to register in global redux store.
 *
 * @type {object}
 * @memberof Mojito.Services.Promotions
 */
export const INITIAL_STATE = PromotionTypes.INITIAL_STATE;

const reducers = {
    updatePromotions: (state, { payload }) => {
        const { data: promotionItem, channel, activeChannels } = payload;
        // Keep promotionsPerChannel in sync with currently subscribed channels
        if (activeChannels) {
            state.promotionsPerChannel = pick(state.promotionsPerChannel, activeChannels);
        }
        if (!promotionItem) {
            delete state.promotionsPerChannel[channel];
        } else {
            state.promotionsPerChannel[channel] = promotionItem.locations;
        }
        const channels = Object.keys(state.promotionsPerChannel);
        state.promotions = mergePromotionChannels(state.promotionsPerChannel, channels);
        state.loadingState = isEmpty(state.promotions) ? UNAVAILABLE : AVAILABLE;
    },
    updatePromotionsFailed: (state, { payload }) => {
        const { channels } = payload;
        state.promotionsPerChannel = omit(state.promotionsPerChannel, ...channels);
        state.promotions = mergePromotionChannels(state.promotionsPerChannel, channels);
        state.loadingState = UNAVAILABLE;
    },
    promotionsPending: state => {
        state.loadingState = PENDING;
    },
    reset: () => {
        return { ...INITIAL_STATE };
    },
};

export const { reducer, actions } = createSlice({
    name: 'promotions',
    initialState: INITIAL_STATE,
    reducers,
});

/**
 * Subscribe client to promotion channels.
 *
 * @function subscribePromotions
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {object} payload - Payload.
 * @param {string} payload.clientId - Id of a subscriber.
 * @param {Array<string>} payload.channels - List of promotion channels to subscribe.
 *
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Subscribe promotions thunk.
 *
 * @memberof Mojito.Services.Promotions.actions
 */
actions.subscribePromotions = payload => {
    return (dispatch, getState) => {
        const { clientId, channels } = payload;

        const onUpdateData = (channel, data) => {
            dispatch(actions.updatePromotions({ channel, data, activeChannels: channels }));
        };
        const onError = () => dispatch(actions.updatePromotionsFailed({ channels }));

        const service = resolveTransport(getState());
        const activeSubscription = subscriptionsRegistry.getSubscription(clientId);
        const activeService = activeSubscription?.service;
        // If service changed, re-subscribe client to all channels using new service
        if (!activeService || service !== activeService) {
            // Only declare promotions pending if this is the first subscription request or if there were never successful data loads before.
            // This is needed to avoid skeleton showup when promotions are getting requested by more and more clients
            if (!ServicesUtils.isContentLoadDone(selectLoadingState(getState()))) {
                dispatch(actions.promotionsPending());
            }
            activeService?.reset();
            service.subscribe({ clientId, channels }, onUpdateData, onError);
            // If channels changed, subscribe client to new channels and unsubscribe from old ones
        } else if (!shallowEqual(channels, activeSubscription?.channels)) {
            const obsoleteChannels = without(activeSubscription.channels, ...channels);
            const newChannels = without(channels, ...activeSubscription.channels);
            if (!isEmpty(obsoleteChannels)) {
                service.unsubscribe({ clientId, channels: obsoleteChannels });
            }
            if (!isEmpty(newChannels)) {
                service.subscribe({ clientId, channels: newChannels }, onUpdateData, onError);
            }
        }
        subscriptionsRegistry.addSubscription(clientId, { service, channels });
    };
};

/**
 * Unsubscribe client from all promotion channels.
 *
 * @function unsubscribePromotions
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {object} clientId - Id of a subscriber.
 *
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Unsubscribe promotions thunk.
 *
 * @memberof Mojito.Services.Promotions.actions
 */
actions.unsubscribePromotions = clientId => {
    return () => {
        const { service, channels } = subscriptionsRegistry.removeSubscription(clientId);
        service.unsubscribe({ clientId, channels });
    };
};

reduxInstance.actionListener.startListening({
    actionCreator: bootstrapActions.dispose,
    effect: () => {
        const services = Object.values(subscriptionsRegistry.subscriptions).map(
            subscription => subscription.service
        );
        uniq(services).forEach(service => service.reset());
        subscriptionsRegistry.reset();
    },
});

const resolveTransport = state => {
    const anonymousService = serviceFactory.getService();
    const authService = serviceFactory.getAuthService() || anonymousService;
    return isLoggedIn(state) ? authService : anonymousService;
};

/**
 * Merge promotion locations for provided channels. This is needed when authenticated user has specific user groups and should receive exclusive promotions for all groups he belongs to.
 * For example, after login we will find out that user belongs to groups: [groupA, groupB]. It means that we need to subscribe to promotion channels mobile_auth, mobile_auth_groupA and mobile_auth_groupB to get all relevant promotions for user using mobile device.
 * After that we will have 3 promotion locations objects in the state.promotionsPerChannel. To get all promotions for the user we need to merge these 3 locations objects into one. This is what this function does.
 *
 * @param {Map<string, object>} promotionsPerChannel - Promotions per channel object.
 * @param {Array<string>} channels - List of channels to merge.
 *
 * @returns {Map<string, Array<Mojito.Services.Promotions.types.Promotion>>} Merged promotion locations.
 * @private
 */
const mergePromotionChannels = (promotionsPerChannel, channels) => {
    // Nothing to merge if only one channel in a list
    if (channels.length === 1) {
        return { ...promotionsPerChannel[channels[0]] };
    }
    return channels
        .map(channel => promotionsPerChannel[channel])
        .reduce((acc, locations) => {
            acc = PromotionUtils.mergeLocations(acc, locations);
            return acc;
        }, {});
};

actionsRegistry.addSubscription(
    PROMOTIONS,
    actions.subscribePromotions,
    actions.unsubscribePromotions
);

reduxInstance.injectReducer(STORE_KEY, reducer);
